import sanitizeHtml from "sanitize-html";
import { Condition, SuccessPageRule } from "../components/FormClient/FormClient.types";
import { Field, StepData } from "../components/FormViewer/FormViewer.types";
import { SessionLayer } from "../interfaces";


/**
 * Check wether rendering client side or server side
 * @returns {Boolean} true if rendering client side
 */

export const sanitizeContent = (content: string) => {
  return sanitizeHtml(content, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(["img"]),
    allowedAttributes: { 
      ...sanitizeHtml.defaults.allowedAttributes,
      "*": [ "style" ]
    },
    parseStyleAttributes: false
  });
};

export const isBrowser = () => window !== undefined;

export const fetchGAClientId = () => {
  const measurementId = "G-KG72SNLJZP";

  const checkAndFetch = () => {
    if (typeof window.gtag === "function") {
      window.gtag("get", measurementId, "client_id", (clientId: string) => {
        if (clientId) {
          window.sessionLayer.lead.marketing_data.ga_client_id = clientId;
        }
      });
      return true;
    }
    return false;
  };

  // Try fetching immediately
  if (checkAndFetch()) return;

  // Retry every second until gtag is available (max 10 tries)
  let attempts = 0;
  const interval = setInterval(() => {
    if (checkAndFetch() || attempts >= 10) {
      clearInterval(interval);
    }
    attempts++;
  }, 1000);
};

export const trackEvent = (eventLabel: string, fieldLabel?: string) => {
  const { formName, uuid } = window.sessionLayer?.lead?.form_client ?? {};

  const event: {
    event: string;
    form: { formName: string | undefined; uuid: string | undefined };
    [key: string]: string | object | undefined;
  } = {
    event: eventLabel,
    form: { formName, uuid }
  };

  if (fieldLabel) {
    event["fieldLabel"] = fieldLabel;
  }

  window.dataLayer?.push(event);
};

export async function getAppBFFUrl() {
  if (process.env.REACT_APP_BASE_URL) {
    return process.env.REACT_APP_BASE_URL;
  }
  
  const defaultLocale = "de-DE";
  return new Promise<string>((resolve) => {
    const waitForConfig = setInterval(() => {
      if (window.FC_CONFIG) {
        const { lead: { form_client: { locale } } } = window.sessionLayer as { lead: SessionLayer };
        const actualLocale: string = locale ?? defaultLocale;

        clearInterval(waitForConfig);
        const baseUrlObject: { [key: string]: string } = window.FC_CONFIG.BASE_URL;
        resolve(baseUrlObject[actualLocale] ?? baseUrlObject[defaultLocale]);
      }
    }, 50);
  });
}

export async function getPublicBucketUrl() {
  if (process.env.REACT_APP_PUBLIC_BUCKET_URL) {
    return process.env.REACT_APP_PUBLIC_BUCKET_URL;
  }
  return new Promise<string>((resolve) => {
    const waitForConfig = setInterval(() => {
      if (window.FC_CONFIG) {
        clearInterval(waitForConfig);
        const baseUrlObject: string = window.FC_CONFIG.PUBLIC_BUCKET_URL;
        resolve(baseUrlObject);
      }
    }, 50);
  });
}

export const getStorageValue = (key: string, defaultValue: string) => {
  const initial: string | null = localStorage.getItem(key);
  return initial || defaultValue;
}

export const redirectToSuccessPage = (defaultSuccessPageUrl: string, rules: Array<SuccessPageRule>, lead_data: any) => {
  if (rules.length === 0) {
    // If there are no rules, redirect to the default success page
    window.location.href = defaultSuccessPageUrl;
    return;
  }

  // Iterate through the rules
  for (const rule of rules) {
    // Check if all conditions in this rule are met
    const conditionsMet = rule.conditions.every(condition => {
      const { field, operator, value } = condition;

      const fieldValue = lead_data[field];
      const hasMatch = field in lead_data;

      return evaluateCondition(operator, hasMatch, fieldValue, value);
    });

    // If all conditions in this rule are met, perform the redirection
    if (conditionsMet) {
      const currentParams = new URLSearchParams(window.location.search);
      const successPageUrl = new URL(rule.successPageUrl);

      // Append any query parameters from the current URL that are not already in the success page URL
      currentParams.forEach((value, key) => {
        if (!successPageUrl.searchParams.has(key)) {
          successPageUrl.searchParams.append(key, value);
        }
      });

      // Redirect to the success page URL
      window.location.href = successPageUrl.toString();
      return;
    }
  }

  window.location.href = defaultSuccessPageUrl;
};

export const getFieldsFormSession = (fieldCrmId: string): string => {
  if (!fieldCrmId) return '';

  const { lead } = window.sessionLayer as { lead: SessionLayer };

  let fieldValue = '';

  // Search in lead_data
  lead?.lead_data?.some((step: StepData) => {
    const { fields, answer } = step;
    if (fields) {
      const matchedField = fields.find((field: Field) => field.field_crm_id === fieldCrmId);
      if (matchedField) {
        fieldValue = matchedField.field_value;
        return true;
      }
    }
    if (answer && (step.settings.crm_field === fieldCrmId || step.label === fieldCrmId)) {
      fieldValue = answer.field_value;
      return true;
    }
    return false;
  });

  // If not found, search in push_data
  if (!fieldValue) {
    lead?.push_data?.some((f: any) => {
      if (f.crm_field === fieldCrmId) {
        fieldValue = f.crm_value;
        return true;
      }
      return false;
    });
  }

  return fieldValue;
};

export const replaceVariablesInText = (text: string, replacement?: string) => {
  if (text) {
    return text.replace(/\$\{\{(.*?)\}\}/g, (_, variableName) => {
      return replacement ?? getFieldsFormSession(variableName);
    });
  }
  return text;
}

export const getVariableFromText = (text: string): string => {
  if (!text) return '';
  return text.replace(/\$\{\{(.*?)\}\}/g, (_, variableName) => variableName.trim());
};

export const getMatchingFieldsFromSession = (conditions: Array<any>) => {

  if (!conditions || conditions.length === 0) return [];
  const { lead } = window.sessionLayer as { lead: SessionLayer };
  const matchingFields: any = [];

  lead?.lead_data?.forEach((step: StepData) => {
    const { fields, answer } = step;
    if (fields) {
      const matchedFields = fields.filter((field: Field) => {
        return returnIfConditionsMatch(conditions, field);
      }).map((field: Field) => {
        return {
          field_crm_id: field.field_crm_id,
          field_value: field.field_value,
          field_session_source: "lead_data"
        }
      });
      matchingFields.push(...matchedFields);
    }
    if (answer) {
      const answerField = { 
        field_crm_id: step.settings.crm_field ?? step.label, 
        field_value: answer.field_value, 
        field_session_source: "lead_data" 
      };
      if (returnIfConditionsMatch(conditions, answerField)) {
        matchingFields.push(answerField);
      }
    }
  });

  lead?.push_data?.forEach((f: any) => {
    const returnField = {
      field_crm_id: f.crm_field,
      field_value: f.crm_value,
      field_session_source: "push_data"
    }
    if (returnIfConditionsMatch(conditions, returnField)) {
      matchingFields.push(returnField);
    }
  });
  
  return matchingFields;
};

export const getFieldFromPushData = (field_crm_id: string) => {
  const { lead } = window.sessionLayer as { lead: SessionLayer };
  const matchingField = lead?.push_data?.find((field: any) => field.crm_field === field_crm_id);

  return matchingField;
}

export const returnIfConditionsMatch = (conditions: Array<Condition>, sessionField: Field) => {
  // Iterate through the conditions
  return conditions.some((condition) => {
    const { operator, value, field } = condition;
    const hasMatch = sessionField.field_crm_id === field;
    const fieldValue = hasMatch && sessionField.field_value;
    const evalOperator = operator || "exists";

    return evaluateCondition(evalOperator, hasMatch, fieldValue, value);
  });
}

export const medicalSurveyCalculatedResult = (product: string) => {
  if (!product) return;
  const medicalSurveryQuestions = [
    "calculated_medical_survey_1",
    "calculated_medical_survey_2",
    "calculated_medical_survey_3",
    "calculated_medical_survey_4",
    "calculated_medical_survey_5"
  ];

  const sum = medicalSurveryQuestions.reduce((total, key) => {
    const value = parseInt(getFieldsFormSession(key), 10);
    return total + (isNaN(value) ? 0 : value); 
  }, 0);

  let resultDescription = "";
  if (sum < 8) {
    resultDescription = "schweren";
  } else if (sum >= 8 && sum <= 11) {
    resultDescription = "moderaten";
  } else if (sum >= 12 && sum <= 16) {
    resultDescription = "milden bis moderaten";
  } else if (sum >= 17 && sum <= 21) {
    resultDescription = "milden";
  } else if (sum >= 22 && sum <= 25) {
    resultDescription = "keine";
  }

  return {
    resultValue: sum.toString(),
    resultDescription
  };
};

const evaluateCondition = (operator: string, hasMatch: boolean, fieldValue: string, value: string) => {
  switch (operator) {
    case "exists":
      return hasMatch
    case "does_not_exist":
      return !hasMatch
    case "is_exact_value":
      return fieldValue === value;
    case "is_not_exact_value":
      return fieldValue !== value;
    case "contains_value":
      return fieldValue && fieldValue.includes(value);
    case "does_not_contain_value":
      return fieldValue && !fieldValue.includes(value);
    default:
      return false; // Invalid operator, consider condition as not met
  }
}